/* eslint-disable sonarjs/no-duplicate-string */
import { SearchResponse } from '@algolia/client-search';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FocusToService } from '@commons/directives/focus-to/focus-to.service';
import { InfoBlockConfig, InfoBlockLinkType, InfoBlockType } from '@commons/info-block/info-block.component';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { CustomMetaService } from '@core/services/meta-service/meta.service';
import { SearchEngineFieldAddress } from '@domains/search-engine/entities/address-search-field';
import { SearchEngineFieldDropdown } from '@domains/search-engine/entities/dropdown-search-field';
import { SearchEngineFields } from '@domains/search-engine/search-engine-field';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { YouthInfoCenterListItem } from '@models/youth-info-center/youth-info-center';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ScrollToConfigOptions, ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { PublicInstitutesWebservice } from '@webservices/public-institutes/public-institutes.webservice.service';
import { City } from '@wizbii/models';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import { CityWebservice } from '@wizbii/webservices';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';

export enum StructuresEnum {
  MISSIONS_LOCALES = 'mission_locale',
  AGENCE_POLE_EMPLOI = 'agency',
  INFOS_JEUNE = 'cij',
}

export const Structures: Record<StructuresEnum, string> = {
  [StructuresEnum.MISSIONS_LOCALES]: 'Missions locales',
  [StructuresEnum.AGENCE_POLE_EMPLOI]: 'Agences Pôle Emploi',
  [StructuresEnum.INFOS_JEUNE]: 'Infos Jeunes',
};
@UntilDestroy()
@Component({
  selector: 'app-youth-info-center-list',
  templateUrl: './youth-info-center-list.component.html',
  styleUrls: ['./youth-info-center-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YouthInfoCenterListComponent implements OnInit, OnDestroy {
  localMissionBlockConfig: InfoBlockConfig = {
    type: InfoBlockType.CENTER,
    title: {
      main: 'Les missions locales proposent un suivi personnalisé',
      emphasis: "pour tous les jeunes jusqu'à 25 ans",
    },
    button: {
      link: ['/', FeaturesRoutingEnum.SeoLocalMission],
      icon: 'arrow_next',
      type: InfoBlockLinkType.ROUTE,
      text: 'En savoir plus',
      ariaLabel: 'Naviguer vers la page Missions Locales',
    },
    img: {
      src: `${environment.wizbiiImages}/accompagnement/logounml.jpg`,
    },
  };

  infoYoungConfig: InfoBlockConfig = {
    type: InfoBlockType.CENTER,
    title: {
      main: "Info Jeunes, le réseau d'accueil et d'information des jeunes en France",
      emphasis: "au service d'une ambition : Explorer les possibles !",
    },

    button: {
      link: 'https://www.infojeunesfrance.org',
      type: InfoBlockLinkType.HREF,
      text: 'En savoir plus',
      ariaLabel: 'Lien vers le site Info Jeunes france',
    },
    img: {
      src: `${environment.wizbiiImages}/accompagnement/info-jeunes.png`,
    },
  };

  trackEvent = trackEventWithAtInternet;

  static readonly ITEMS_PER_PAGE = 10;

  private readonly baseSkltn = {
    'border-radius': '1.25rem',
    height: '9.625rem',
  };

  readonly skltnCardMobile = {
    ...this.baseSkltn,
    width: '100%',
    height: '21.875rem',
  };
  readonly skltnCard = {
    ...this.baseSkltn,
    width: '72.5rem',
    height: '9.625rem',
  };

  youthInfoCenterSearchEngineConfig?: SearchEngineFields;

  loading$ = new BehaviorSubject<boolean>(true);

  youthInfoCenters$ = new BehaviorSubject<YouthInfoCenterListItem[]>([]);
  nbYouthInfoCenters$ = new BehaviorSubject<number>(0);
  shouldFocusItem$ = new BehaviorSubject<boolean>(false);
  hasSearched$ = new BehaviorSubject<boolean>(false);

  page$?: Observable<number>;
  address$!: Observable<City | null>;

  readonly focusBaseId = 'youth-info-center-list-card';
  structures$?: Observable<string>;

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  get isMobileOrTablet$(): Observable<boolean> {
    return this.breakpointsService.isMobileOrTablet$;
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly cityWebservice: CityWebservice,
    private readonly publicInstitutesWebservice: PublicInstitutesWebservice,
    private readonly breakpointsService: BreakpointsService,
    private readonly scrollToService: ScrollToService,
    private readonly element: ElementRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly metaService: CustomMetaService,
    private readonly focusService: FocusToService
  ) {}

  ngOnInit(): void {
    this.metaService.updateForSeo({
      title: 'Je contacte un conseiller pour être accompagné dans mes démarches et mon parcours',
    });

    this.youthInfoCenterSearchEngineConfig = this.initSearchEngineConfig();

    this.page$ = this.route.queryParamMap.pipe(
      tap((data) => this.shouldFocusItem$.next(!!data.get('page'))),
      map((data) => data.get('page') || '1'),
      map((page) => parseInt(page, 10)),
      distinctUntilChanged()
    );

    this.address$ = this.route.queryParamMap.pipe(
      map((params) => params.get('cityPlaceId')),
      distinctUntilChanged(),
      switchMap((cityPlaceId) => (cityPlaceId ? this.cityWebservice.get(cityPlaceId) : of(null))),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.structures$ = this.route.queryParamMap.pipe(
      map((data) => data.get('structures') as string),
      distinctUntilChanged()
    );

    combineLatest([
      this.page$.pipe(distinctUntilChanged()),
      this.address$.pipe(distinctUntilChanged()),
      this.shouldFocusItem$.pipe(distinctUntilChanged()),
      this.structures$.pipe(distinctUntilChanged()),
    ])
      .pipe(
        filter(([_, address, __, ___]: [number, City | null, boolean, string]) => !!address),
        untilDestroyed(this),
        tap(() => this.loading$.next(true)),
        switchMap(([page, city, shouldFocus, structures]) => {
          return this.publicInstitutesWebservice
            .getInstitutes({
              ...(city ? { aroundLatLng: `${city.geo.lat}, ${city.geo.lon}`, aroundRadius: 20000 } : {}),
              ...{
                offset: (page - 1) * YouthInfoCenterListComponent.ITEMS_PER_PAGE,
                length: YouthInfoCenterListComponent.ITEMS_PER_PAGE,
                filters: this.buildFilters(structures),
              },
            })
            .pipe(map((result) => [result, shouldFocus] as [SearchResponse<YouthInfoCenterListItem>, boolean]));
        })
      )
      .subscribe({
        next: ([results, shouldFocus]) => {
          this.hasSearched$.next(true);
          this.youthInfoCenters$.next(results.hits);
          this.nbYouthInfoCenters$.next(results.nbHits);
          this.loading$.next(false);

          if (shouldFocus) {
            this.focusItem();
          }
        },
      });
  }

  buildFilters(structures?: string): string | undefined {
    return structures
      ? structures
          .split(',')
          .map((structure) => `type:${structure}`)
          .join(' OR ')
      : undefined;
  }

  initSearchEngineConfig(): SearchEngineFields {
    return {
      mainField: new SearchEngineFieldAddress(
        {
          placeholder: 'Adresse postale',
          ariaLabel:
            'Je cherche un accompagnement, champ texte avec saisie semi automatique pour renseigner son adresse',
          icon: 'search',
        },
        { required: true }
      ),
      submainFields: [
        new SearchEngineFieldDropdown(
          {
            name: 'structures',
            placeholder: 'Structures',
            ariaLabel:
              'Recherche de missions, liste déroulante pour sélectionner le type de structure proposant la mission',
            icon: 'work_case',
          },
          { value: Structures },
          { flexValue: 0.5 }
        ),
      ],
      redirectUri: FeaturesRoutingEnum.YouthInfoCenter,
      trackEventName: 'Accompagnement',
      liveSearch: false,
    };
  }

  focusItem(): void {
    this.cdr.detectChanges();

    this.scrollToService.scrollTo({ target: `${this.focusBaseId}-0`, offset: -100 });
    const elmToFocus = this.element.nativeElement.querySelector(`#${this.focusBaseId}-0`);

    if (elmToFocus) {
      elmToFocus.focus();
    }
  }

  scrollToId(id: string): void {
    const config: ScrollToConfigOptions = {
      target: id,
      offset: -100,
      easing: 'easeInOutQuad',
    };

    this.scrollToService.scrollTo(config);
    this.focusService.focusTo(id);
  }

  trackByYouthInfoCenterId(_: number, item: YouthInfoCenterListItem): string {
    return item.objectID;
  }

  onTrackerInfoBlockClicked(action: string): void {
    this.trackEvent({ category: 'Accompagnement', action, type: 'navigation' });
  }

  ngOnDestroy(): void {
    this.loading$.complete();
    this.hasSearched$.complete();
    this.youthInfoCenters$.complete();
    this.shouldFocusItem$.complete();
    this.metaService.restoreForSeo();
  }
}
